import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { LISTADOS, columnasDeudores, formatoMesPlanilla, irADetalleCobro } from './definiciones';
import TablaListado from '../../components/Listados/TablaListado';
import AccesosListados from '../../components/Listados/AccesosListados';
import ModalDetalleCobro from '../../components/Cobranzas/ModalDetalleCobro';
import PageHeader from '../../components/ui/PageHeader';
import ModalRecordarCuotaVencida from '../../components/Cobranzas/ModalRecordarCuotaVencida';
import {
    cambiarRecordatorioDeudores, cerrarDeudoresPlanilla, getMesesDeudoresPlanilla,
} from '../../services/cobranzaService';
import useFiltroPersistente from '../../hooks/useFiltroPersistente';

const DEF = LISTADOS.deudores;

const ORIGENES = {
    TODOS: { etiqueta: 'Todos', migrado: undefined },
    SISTEMA: { etiqueta: 'Del sistema', migrado: false },
    PLANILLA: { etiqueta: 'De la planilla', migrado: true },
};

/**
 * Deudores. Además de los cobros en cuotas del sistema, están los de la planilla NUEVO DEUDORES EN
 * CUOTA (V273): nacen con el recordatorio automático apagado y se eligen por el mes del bloque del
 * Excel para activarlo o para darlos por cerrados (pasan a Archivo y salen de Deudores). Pedido del
 * usuario, 24/25-oct-2026.
 */
export default function ListadoDeudores() {
    const navigate = useNavigate();
    const [idCobroAbierto, setIdCobroAbierto] = useState(null);
    const [recargarToken, setRecargarToken] = useState(0);
    // Recordatorio de cuota vencida (V263): el automático avisa antes del vencimiento; después,
    // el que recuerda es el operador, con este botón.
    const [cobroARecordar, setCobroARecordar] = useState(null);
    const [origen, setOrigen] = useFiltroPersistente('listadoDeudores.origen', 'TODOS');
    const [mes, setMes] = useFiltroPersistente('listadoDeudores.mes', '');
    const [meses, setMeses] = useState([]);
    const [trabajando, setTrabajando] = useState(false);

    const recargar = useCallback(() => setRecargarToken((t) => t + 1), []);

    useEffect(() => {
        let vigente = true;
        (async () => {
            try {
                const datos = await getMesesDeudoresPlanilla();
                if (vigente) setMeses(datos || []);
            } catch {
                if (vigente) setMeses([]);
            }
        })();
        return () => { vigente = false; };
    }, [recargarToken]);

    const cambiarRecordatorio = useCallback(async (seleccion, activo) => {
        try {
            const { cambiados } = await cambiarRecordatorioDeudores({ ...seleccion, activo });
            toast.success(activo
                ? `Recordatorio automático activado (${cambiados}).`
                : `Recordatorio automático apagado (${cambiados}).`);
            recargar();
        } catch (err) {
            toast.error(err.response?.data?.mensaje || 'No se pudo cambiar el recordatorio.');
        }
    }, [recargar]);

    const columnas = useMemo(() => columnasDeudores({
        onRecordar: setCobroARecordar,
        onCambiarRecordatorio: (row, activo) => cambiarRecordatorio({ ids: [row.idCobro] }, activo),
    }), [cambiarRecordatorio]);

    const filtros = useMemo(() => {
        const elegido = ORIGENES[origen] || ORIGENES.TODOS;
        return { migrado: mes ? true : elegido.migrado, mesPlanilla: mes || undefined };
    }, [origen, mes]);
    const fetchPage = useCallback((page, size, texto) => DEF.fetchPage(page, size, texto, filtros), [filtros]);

    const mesElegido = meses.find((m) => m.mes === mes);

    const cerrarMes = async () => {
        if (!mesElegido) return;
        if (!window.confirm(`¿Dar por cerradas las ${mesElegido.total} deudas de ${formatoMesPlanilla(mes)}? `
            + 'Pasan a Archivo y salen de Deudores (quedan de baja con motivo). Se pueden reabrir de a una desde el cobro.')) return;
        setTrabajando(true);
        try {
            const { cerrados } = await cerrarDeudoresPlanilla({ mes });
            toast.success(`${cerrados} deudas cerradas y archivadas.`);
            setMes('');
            recargar();
        } catch (err) {
            toast.error(err.response?.status === 403
                ? 'Darlas por cerradas lo hace administración.'
                : err.response?.data?.mensaje || 'No se pudieron cerrar.');
        } finally {
            setTrabajando(false);
        }
    };

    const alEnviar = (resultado) => {
        setCobroARecordar(null);
        recargar();
        if (resultado?.quedoEnCola) {
            toast.warning('WhatsApp está desconectado: el recordatorio quedó en cola y sale cuando se reconecte.');
        } else {
            toast.success('Recordatorio enviado por WhatsApp. Quedó en la bitácora.');
        }
    };

    const filtrosExtra = (
        <div className="d-flex gap-2 flex-wrap align-items-center">
            <select className="form-select form-select-sm" style={{ width: 'auto' }} aria-label="Origen"
                    value={origen} onChange={(e) => { setOrigen(e.target.value); if (e.target.value !== 'PLANILLA') setMes(''); }}>
                {Object.entries(ORIGENES).map(([clave, o]) => <option key={clave} value={clave}>{o.etiqueta}</option>)}
            </select>
            {meses.length > 0 && (
                <select className="form-select form-select-sm" style={{ width: 'auto' }} aria-label="Mes de la planilla"
                        value={mes} onChange={(e) => { setMes(e.target.value); if (e.target.value) setOrigen('PLANILLA'); }}>
                    <option value="">Mes de la planilla: todos</option>
                    {meses.map((m) => (
                        <option key={m.mes || 'sin-mes'} value={m.mes || ''}>
                            {formatoMesPlanilla(m.mes)} · {m.total}{m.conRecordatorio ? ` (${m.conRecordatorio} con recordatorio)` : ''}
                        </option>
                    ))}
                </select>
            )}
        </div>
    );

    const chipsExtra = [
        ...(origen !== 'TODOS' && !mes ? [{ key: 'origen', label: ORIGENES[origen]?.etiqueta, onQuitar: () => setOrigen('TODOS') }] : []),
        ...(mes ? [{ key: 'mes', label: `Planilla: ${formatoMesPlanilla(mes)}`, onQuitar: () => setMes('') }] : []),
    ];

    return (
        <div className="container-fluid mt-4 listado-pagina area-cobranzas">
            <PageHeader
                variante="plano"
                icono={DEF.icono}
                titulo={DEF.titulo}
                descripcion={DEF.subtitulo}
            />

            <AccesosListados actual={DEF.clave} />

            {mesElegido && (
                <div className="alert alert-light border rounded-3 d-flex flex-wrap align-items-center gap-2 py-2">
                    <span className="me-auto" style={{ fontSize: '0.9rem' }}>
                        <strong>{mesElegido.total}</strong> deudores de la planilla de <strong>{formatoMesPlanilla(mes)}</strong>
                        {' · '}{mesElegido.conRecordatorio} con recordatorio automático
                    </span>
                    <button type="button" className="btn btn-sm btn-outline-success rounded-pill" disabled={trabajando}
                            onClick={() => cambiarRecordatorio({ mes }, true)}>
                        <i className="bi bi-bell me-1" />Activar recordatorio a todos
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-secondary rounded-pill" disabled={trabajando}
                            onClick={() => cambiarRecordatorio({ mes }, false)}>
                        <i className="bi bi-bell-slash me-1" />Apagarlo
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-danger rounded-pill" disabled={trabajando} onClick={cerrarMes}>
                        <i className="bi bi-archive me-1" />Dar por cerradas y archivar
                    </button>
                </div>
            )}

            <TablaListado
                claveFiltro="listadoDeudores"
                columnas={columnas}
                fetchPage={fetchPage}
                onRowClick={(row) => irADetalleCobro(row, navigate, setIdCobroAbierto)}
                rowKey={(row) => row.idCobro}
                rowClassName={DEF.rowClassName}
                dependencias={[recargarToken, filtros]}
                reiniciarPaginaCon={`${origen}|${mes}`}
                filtrosExtra={filtrosExtra}
                chipsExtra={chipsExtra}
                onLimpiarExtra={() => { setOrigen('TODOS'); setMes(''); }}
            />

            <ModalDetalleCobro
                open={!!idCobroAbierto}
                idCobro={idCobroAbierto}
                onClose={() => setIdCobroAbierto(null)}
                onCambio={recargar}
            />

            <ModalRecordarCuotaVencida
                show={!!cobroARecordar}
                cobro={cobroARecordar}
                onCerrar={() => setCobroARecordar(null)}
                onEnviado={alEnviar}
            />
        </div>
    );
}
